/* eslint-disable @typescript-eslint/member-ordering */
import {
  Component,
  inject,
  OnInit,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, mergeMap, startWith } from 'rxjs/operators';

import { translate } from '@jsverse/transloco';

import {
  ADDITIONAL_THRID_PARTY_USAGE,
  CUSTOM_DATA_PRIVACY,
  CUSTOM_IMPRINT_DATA,
  DATA_SOURCE,
  PERSON_RESPONSIBLE,
  PURPOSE,
  STORAGE_PERIOD,
  TERMS_OF_USE,
} from './legal.model';
import { LegalPath } from './legal-route-path.enum';
@Component({
  selector: 'schaeffler-legal',
  templateUrl: './legal.component.html',
  styleUrls: ['./legal.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LegalComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  public personResponsible = inject(PERSON_RESPONSIBLE);
  public purpose$ = inject<Observable<any>>(PURPOSE, {
    optional: true,
  });
  public dataSource$ = inject<Observable<any>>(DATA_SOURCE, {
    optional: true,
  });
  public storagePeriod$ = inject<Observable<any>>(STORAGE_PERIOD, {
    optional: true,
  });
  public additionalThirdPartyUsage$ = inject<Observable<string>>(
    ADDITIONAL_THRID_PARTY_USAGE,
    { optional: true }
  );
  public customDataPrivacy$ = inject<Observable<string>>(CUSTOM_DATA_PRIVACY, {
    optional: true,
  });
  public customImprintData$ = inject<Observable<any>>(CUSTOM_IMPRINT_DATA, {
    optional: true,
  });
  public termsOfUse$ = inject<Observable<any>>(TERMS_OF_USE, {
    optional: true,
  });

  public LegalRoutePath = LegalPath;
  public translationContent$!: Observable<string>;
  public lastPath$!: Observable<string>;

  public ngOnInit(): void {
    this.lastPath$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      startWith({ url: this.route.snapshot.url[0].path } as NavigationEnd),
      map((event) => (event as NavigationEnd).url.split('/').pop() as string)
    );

    this.translationContent$ = this.lastPath$.pipe(
      mergeMap((path) => {
        if (path === LegalPath.DataprivacyPath && this.customDataPrivacy$) {
          return this.customDataPrivacy$;
        }

        if (path === LegalPath.ImprintPath && this.customImprintData$) {
          return this.customImprintData$;
        }

        const defaultTranslateOptions = {
          responsible: translate('responsibleIntro', {
            personResponsible: this.personResponsible,
          }),
          personResponsible: this.personResponsible,
        };

        return combineLatest([
          this.purpose$ ?? of(''),
          this.dataSource$ ?? of(translate('defaultDataSource')),
          this.storagePeriod$ ?? of(translate('defaultPeriod')),
          this.additionalThirdPartyUsage$ ?? of(''),
        ]).pipe(
          map(([purpose, dataSource, storagePeriod, thirdPartyUsage]) =>
            translate(path, {
              ...defaultTranslateOptions,
              purpose,
              dataSource,
              storagePeriod,
              thirdPartyUsage,
            })
          )
        );
      })
    );
  }

  public navigate(): void {
    this.router.navigate(['/']);
  }
}
